// Thin axios wrapper around the real ARC Beacon backend.
//
// Base URL comes from REACT_APP_BACKEND_URL (see .env) so the same build
// can point at a local Docker backend, staging, or production without a
// code change. The JWT issued by POST /api/auth/login is attached to every
// request automatically; a 401 from anywhere but the login call itself
// means the session is no longer valid, so we clear it and bounce to /login
// rather than let the app sit in a half-authenticated state.
import axios from "axios";

// `??` and not `||` on purpose. In production the frontend and the API are
// served from the same origin behind one nginx (`/` -> the build, `/api` ->
// gunicorn), so the correct base URL is the EMPTY STRING — requests go out
// as relative "/api/...". An empty string is falsy, so `||` would silently
// fall through to localhost:5000 and every call would fail in the browser.
// `??` only falls back when the variable is genuinely unset (local dev).
export const BASE_URL = process.env.REACT_APP_BACKEND_URL ?? "http://localhost:5000";
const TOKEN_KEY = "arc_token";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    // When BASE_URL is an ngrok tunnel (used during local Bouncie testing),
    // ngrok's free-tier interstitial intercepts any request that looks
    // like it came from a browser and returns an HTML warning page with
    // no CORS headers instead of proxying it — which the browser then
    // reports as an opaque network error, not a normal HTTP error. This
    // header (documented by ngrok) skips that page. It's a no-op against
    // a real backend, so it's safe to always send.
    "ngrok-skip-browser-warning": "true",
  },
});

apiClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const isLoginCall = error.config?.url?.includes("/api/auth/login");
    if (error.response?.status === 401 && !isLoginCall) {
      clearToken();
      localStorage.removeItem("arc_auth");
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        window.location.assign("/login");
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
